<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_ECHANNEL_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="batchUploadTracerTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge></template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >

      <validation-observer ref="batchUploadPostingTracerRules">
        <b-form>
          <b-row class="match-height">
            <b-col md="6">
              <InputField
                id="tracking-reference"
                v-model="filter.trackingReference"
                :label="$t('Tracking Reference')"
                :placeholder="$t('Enter Tracking Reference')"
                type="text"
              />
            </b-col>

            <!-- Posting Reference -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.postingReferenceNumber"
                :label="$t('Posting Reference')"
                :placeholder="$t('Enter Posting Reference')"
                type="text"
              />
            </b-col>
            <!-- Posting Reference -->
            <b-col md="6">
              <InputField
                id="initiator"
                v-model="filter.initiator"
                :label="$t('Initiator')"
                :placeholder="$t('Enter Initiator')"
                type="text"
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="approver"
                v-model="filter.approver"
                :label="$t('Approver')"
                :placeholder="$t('Enter approver')"
                type="text"
              />
            </b-col>

            <!-- Status -->
            <b-col md="6">
              <SelectField
                id="status"
                v-model="filter.status"
                :reduce="option => option.value"
                :label="$t('Status')"
                :placeholder="$t('Select')"
                :options="translatedStatusOptions"
                item="label"
              />
            </b-col>
            <!-- Status -->
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BForm, BBadge,
  BModal, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import XLSX from 'xlsx'
import {
  toBalance, toFormatAmount, formatAmount,
} from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import Button from '../../components/button/Button.vue'
import SelectField from '../../forms/SelectField.vue'
import getStatusColor from '@/helpers/getStatusColor'
import toNumber from '@/helpers/toNumber'
import {
  toDateWithYear, toDateWithCustomTime,
} from '@/helpers/formatDate'
import { postingPermissions } from '@/helpers/permissions'

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'InActive', label: 'InActive' },
]
export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    BModal,
    BDropdown,
    BDropdownItem,
    BForm,
    BBadge,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toBalance,
      getStatusColor,
      toFormatAmount,
      formatAmount,
      toDateWithYear,
      toDateWithCustomTime,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: 'Save',
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Batch Upload Posting Tracer'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'trackingReference', sortable: true, label: 'Tracking Reference' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Reference' },
        { key: 'message', sortable: true, label: 'Message' },
        { key: 'initiator', sortable: true, label: 'Initiator' },
        { key: 'approver', sortable: true, label: 'Approver' },
        { key: 'status', sortable: true, label: 'Status' },
      ],

      filter: {
        trackingReference: '',
        postingReferenceNumber: '',
        initiator: '',
        approver: '',
      },
      // statusOptions: [
      //   { value: 'Active', label: 'Active' },
      //   { value: 'InActive', label: 'InActive' }
      // ],
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        maxDate: 'today',
        defaultDate: '',
      },
      reportData: [],
      allSearchedData: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedStatusOptions() {
      return statusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map((item, index) => ({
        'S/N': index + 1,
        'Tracking Reference': item.trackingReference,
        'Reference Number': item.postingReferenceNumber,
        Message: item.message,
        Initiator: item.initiator,
        Approver: item.approver,
        Status: item.status,
      }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchBatchUploadPostingTracer()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchBatchUploadPostingTracer()
    },
  },
  mounted() {
    this.fetchBatchUploadPostingTracer()
  },
  methods: {
    ...mapActions({
      getBatchUploadTracer: 'reports/getBatchUploadTracer',
    }),

    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchBatchUploadPostingTracer()
    },

    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.batchUploadPostingTracerRules.validate().then(async success => {
        if (success) {
          this.fetchBatchUploadPostingTracer()
        }
      })
    },
    async fetchBatchUploadPostingTracer() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getBatchUploadTracer({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: postingPermissions.VIEW_BATCH_GL_CUSTOMER_UPLOAD_POSTING,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.reportData = data.data
          this.totalCount = data.recordCount
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.getBatchUploadTracer({
          search: this.filter,
          page: 1,
          limit: 1000000,
          permission: postingPermissions.VIEW_BATCH_GL_CUSTOMER_UPLOAD_POSTING,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += [
            [orgName],
            ['Batch Upload Posting Tracer'],
            [],

            Object.keys(arrData[0]).join(','),
            ...arrData.map(item => Object.values(item).join(',')),
          ]
            .join('\n')
            .replace(/(^\[)|(\]$)/gm, '')

          const dataValue = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', dataValue)
          link.setAttribute('download', 'Batch Upload Posting Tracer.csv')
          link.click()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.getBatchUploadTracer({
          search: this.filter,
          page: 1,
          limit: 1000000,
          permission: postingPermissions.VIEW_BATCH_GL_CUSTOMER_UPLOAD_POSTING,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          const filename = 'Batch-Upload-Posting-Tracer.xlsx'
          const headerLists = [
            Object.keys(arrData[0]),
          ]

          const Heading = [
            [orgName],
            ['Batch Upload Posting Tracer'],
            [],
            headerLists[0], // Headers lists
          ]
          const Data = this.allSearchedData.map((item, index) => ({
            'S/N': index + 1,
            'Tracking Reference': item.trackingReference,
            'Reference Number': item.postingReferenceNumber,
            Message: item.message,
            Initiator: item.initiator,
            Approver: item.approver,
            Status: item.status,
          }))
          const ws = XLSX.utils.aoa_to_sheet(Heading)
          XLSX.utils.sheet_add_json(ws, Data, {
            skipHeader: true,
            origin: -1,
          })
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Batch Upload Posting Tracer')
          XLSX.writeFile(wb, filename)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    formatNumber: toNumber,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
